/**
 * Component: HeroVideoDialog
 * Displays a clickable video thumbnail that opens a modal with an animated
 * transition showing an embedded video. Supports multiple animation styles.
 *
 * Props:
 * - className?: string - container class names
 * - animationStyle?: 'from-center' | 'from-top' | 'from-bottom'
 * - videoSrc: string - iframe src URL
 * - thumbnailSrc: string - image src for the thumbnail
 * - thumbnailAlt?: string - alt text for the thumbnail
 */
import React, { useState } from 'react';
import { Play, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const HeroVideoDialog = ({
  className = "",
  animationStyle = "from-center",
  videoSrc,
  thumbnailSrc,
  thumbnailAlt = "Video thumbnail",
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const getAnimationVariants = () => {
    switch (animationStyle) {
      case "from-center":
        return {
          initial: { scale: 0, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0, opacity: 0 }
        };
      case "from-top":
        return {
          initial: { y: -300, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { y: -300, opacity: 0 }
        };
      case "from-bottom":
        return {
          initial: { y: 300, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { y: 300, opacity: 0 }
        };
      default:
        return {
          initial: { scale: 0, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0, opacity: 0 }
        };
    }
  };

  const variants = getAnimationVariants();

  return (
    <>
      {/* Video Thumbnail */}
      <div className={`relative cursor-pointer group ${className}`} onClick={openModal} {...props}>
        <div className="relative overflow-hidden rounded-lg shadow-lg">
          <img
            src={thumbnailSrc}
            alt={thumbnailAlt}
            className="w-full h-auto"
          />
          
          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center justify-center w-16 h-16 bg-white bg-opacity-95 rounded-full shadow-lg group-hover:bg-opacity-100 group-hover:shadow-xl transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:bg-gradient-to-br group-hover:from-white group-hover:to-purple-50">
              <Play className="w-6 h-6 text-purple-600 ml-0.5 transition-all duration-300 ease-out group-hover:text-purple-700" fill="currentColor" />
            </div>
          </div>
          

        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
            onClick={closeModal}
          >
            <motion.div
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
                duration: 0.3
              }}
              className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Video iframe */}
              <iframe
                src={videoSrc}
                title="Video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HeroVideoDialog; 